import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical";
import { type JSX } from "react";

export const broadcastWords = ["here", "channel", "everyone"] as const;

export type BroadcastRange = (typeof broadcastWords)[number];

type SerializedBroadcastNode = Spread<
  {
    // the range is one of the broadcast words
    range: BroadcastRange;
  },
  SerializedLexicalNode
>;

export class BroadcastNode extends DecoratorNode<JSX.Element> {
  __range: BroadcastRange;
  __display: string;

  static getType(): string {
    return "broadcast";
  }

  constructor(range: BroadcastRange, key?: NodeKey) {
    super(key);
    this.__range = range;
    this.__display = BroadcastNode.makeDisplay(range);
  }

  static clone(node: BroadcastNode): BroadcastNode {
    return new this(node.__range, node.__key);
  }

  decorate(): JSX.Element {
    return <>{this.__display}</>;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "reference broadcast";
    return span;
  }
  exportDOM(): DOMExportOutput {
    const span = document.createElement("span");
    span.className = "reference broadcast";
    span.textContent = this.__display;
    return {
      element: span,
    };
  }

  static importDOM(): DOMConversionMap<HTMLSpanElement> | null {
    return {
      span: (element) => {
        const textContent = element.textContent;
        if (
          !textContent.startsWith("@") ||
          !broadcastWords.includes(textContent.substring(1) as BroadcastRange)
        ) {
          return null;
        }
        return {
          conversion: (): DOMConversionOutput => {
            return {
              node: $createChatReferenceBroadcastNode(textContent.substring(1) as BroadcastRange),
            };
          },
          priority: 0,
        };
      },
    };
  }

  updateDOM(): boolean {
    // force a re-create
    return true;
  }

  exportJSON(): SerializedBroadcastNode {
    return {
      ...super.exportJSON(),
      type: "broadcast",
      version: 1,
      range: this.__range,
    };
  }

  static importJSON(serializedNode: SerializedBroadcastNode): BroadcastNode {
    return new BroadcastNode(serializedNode.range);
  }

  updateFromJSON(serializedNode: SerializedBroadcastNode): this {
    super.updateFromJSON(serializedNode).setRange(serializedNode.range);
    return this;
  }

  isInline(): boolean {
    return true;
  }

  setRange(range: BroadcastRange): this {
    this.__range = range;
    this.__display = BroadcastNode.makeDisplay(range);
    return this;
  }

  getRange(): BroadcastRange {
    return this.__range;
  }

  private static makeDisplay(range: string): string {
    return `@${range}`;
  }

  getDisplay(): string {
    return this.__display;
  }
}

export function $createChatReferenceBroadcastNode(range: string): BroadcastNode {
  if (!broadcastWords.includes(range as BroadcastRange)) {
    throw new Error(`Invalid range: ${range}. Must be one of ${broadcastWords.join(", ")}`);
  }
  const node = new BroadcastNode(range as BroadcastRange);

  return $applyNodeReplacement(node);
}

export function $isBroadcastNode(node: unknown): node is BroadcastNode {
  return node instanceof BroadcastNode;
}
